import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { SEOWrapper } from "@/components/SEOWrapper";
import { CTAFinal } from "@/components/home/CTAFinal";
import { usePageSeo } from "@/hooks/usePageSeo";
import { ORG_REF } from "@/data/entity";

import {
  LocalSEOHubHero,
  LocalSEOHubSummary,
  LocalSEOHowItWorks,
  LocalSEOLocationsGrid,
  LocalSEOServices,
  LocalSEOCases,
  LocalSEOHubFAQ
} from "@/components/local-seo";

type Language = 'es' | 'en';

const LocalSEOHubPage = () => {
  // El idioma se deriva de la URL (/seo-local vs /en/local-seo), nunca de
  // estado React: antes la ruta inglesa servía el contenido en español.
  const { language, url, onLanguageChange } = usePageSeo();

  const meta = {
    es: {
      title: "SEO Local para Empresas B2B | Houston y Texas - Luis ROC",
      description: "Servicios de SEO local para empresas B2B en Houston, The Woodlands, Spring, Katy y más. Posiciona tu negocio en Google Maps y búsquedas locales. Consultor SEO con experiencia local.",
      keywords: [
        "SEO local Houston",
        "SEO local empresas B2B",
        "consultor SEO local Texas",
        "posicionamiento Google Maps",
        "SEO local The Woodlands",
        "SEO local Spring Texas"
      ]
    },
    en: {
      title: "Local SEO for B2B Companies | Houston and Texas - Luis ROC",
      description: "Local SEO services for B2B companies in Houston, The Woodlands, Spring, Katy and more. Position your business in Google Maps and local searches. SEO consultant with local experience.",
      keywords: [
        "local SEO Houston",
        "local SEO B2B companies",
        "local SEO consultant Texas",
        "Google Maps positioning",
        "local SEO The Woodlands",
        "local SEO Spring Texas"
      ]
    }
  };

  const currentMeta = meta[language];

  // Service con área de servicio. NO se declara una LocalBusiness por ciudad:
  // la única ubicación física es Shenandoah, TX (ver src/data/entity.ts).
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": language === 'es' ? "Consultoría de SEO Local" : "Local SEO Consulting",
    "description": language === 'es' 
      ? "Servicios de SEO local para empresas B2B en Houston y Texas."
      : "Local SEO services for B2B companies in Houston and Texas.",
    "url": url,
    "serviceType": "Local SEO",
    "provider": ORG_REF,
    "areaServed": [
      { "@type": "City", "name": "Houston" },
      { "@type": "City", "name": "The Woodlands" },
      { "@type": "City", "name": "Spring" },
      { "@type": "City", "name": "Katy" },
      { "@type": "City", "name": "Sugar Land" },
      { "@type": "City", "name": "Conroe" }
    ],
  };

  // Service Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": language === 'es' ? "SEO Local" : "Local SEO",
    "description": language === 'es'
      ? "Servicio de optimización para búsquedas locales y Google Maps."
      : "Optimization service for local searches and Google Maps.",
    "provider": ORG_REF,
    "areaServed": {
      "@type": "State",
      "name": "Texas"
    },
    "serviceType": "Local SEO"
  };


  return (
    <SEOWrapper
      title={currentMeta.title}
      description={currentMeta.description}
      keywords={currentMeta.keywords}
      url={url}
      language={language}
      pageType="website"
      summary={
        language === "es"
          ? "SEO local para empresas B2B en Houston y Texas. Posiciona tu negocio en Google Maps y búsquedas locales."
          : "Local SEO for B2B companies in Houston and Texas. Position your business in Google Maps and local searches."
      }
      keyPoints={
        language === "es"
          ? [
              "SEO local especializado para B2B",
              "Google Business Profile optimizado",
              "Búsquedas locales con intención comercial",
              "Houston, The Woodlands, Spring, Katy",
              "Resultados medibles",
            ]
          : [
              "Specialized local SEO for B2B",
              "Optimized Google Business Profile",
              "Local searches with commercial intent",
              "Houston, The Woodlands, Spring, Katy",
              "Measurable results",
            ]
      }
    >
      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />

        <main>
          {/* Section 1: Hero */}
          <LocalSEOHubHero language={language} />
          
          {/* Section 2: Summary LLM-friendly */}
          <LocalSEOHubSummary language={language} />
          
          {/* Section 3: How It Works */}
          <LocalSEOHowItWorks language={language} />
          
          {/* Section 4: Locations Grid */}
          <LocalSEOLocationsGrid language={language} />
          
          {/* Section 5: Services */}
          <LocalSEOServices language={language} />
          
          {/* Section 6: Cases */}
          <LocalSEOCases language={language} />
          
          {/* Section 7: FAQ */}
          <LocalSEOHubFAQ language={language} />
          
          {/* Section 8: CTA Final */}
          <CTAFinal language={language} />
        </main>

        <Footer language={language} />
        <WhatsAppFloat />
      </div>
      
      {/* Schemas */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
    </SEOWrapper>
  );
};

export default LocalSEOHubPage;
